import { useState, type MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { DocumentRecord } from '../../../models/Document'

import { selectDocumentTypeById } from '../../../store/processDocument/documentTypes'
import { useAppSelector } from '../../../store/hooks'

import { DocumentProcessingStatus } from '../DocumentProcessingStatus/DocumentProcessingStatus'
import { DeleteDocumentModal } from '../DocumentDeleteModal'
import { formatFileSize } from '../DocumentUpload/utils'
import { getDocumentFile } from '../utils'
import { DocumentFileIcon } from '../DocumentFileIcon'
import { useDocumentFile } from '../hooks'

interface DocumentRowProps {
  document: DocumentRecord
}

export const DocumentRow: React.FC<DocumentRowProps> = ({ document }) => {
  const { files, typeProcessing } = document
  const { typeId } = typeProcessing

  const file = getDocumentFile(document)

  const [show, setShow] = useState<boolean>(false)

  const navigate = useNavigate()

  const documentType = useAppSelector((state) => (typeId ? selectDocumentTypeById(state, typeId) : null))

  const { fileInfo, isLoading } = useDocumentFile(files)

  const showDeleteModal = (e: MouseEvent) => {
    e.stopPropagation()
    setShow(true)
  }

  const hideDeleteModal = () => {
    setShow(false)
  }

  const handleNavigate = () => {
    navigate(`/documents/${document.id}`)
  }

  return (
    <>
      {show ? <DeleteDocumentModal show={show} onHide={hideDeleteModal} document={document} /> : null}
      <div
        key={document.id}
        className='document-row cursor-pointer px-3 zocks-py-0.75 d-flex align-items-center gap-3 zocks-border-0.75 border zocks-border-color-gray-400'
        onClick={handleNavigate}
      >
        <div className='document-upload-queue-icon-container d-flex align-items-center justify-content-center flex-shrink-0 rounded-3 border zocks-border-color-gray-300'>
          <DocumentFileIcon contentType={file?.contentType} className='zocks-text-gray-700 fs-5' />
        </div>
        <div className='d-flex align-items-center gap-3 flex-grow-1 mw-0'>
          <span className='bold text-truncate mw-0'>{document.name}</span>
          {!isLoading ? (
            fileInfo ? (
              <span className='paragraph-small text-nowrap zocks-text-gray-700 flex-shrink-0'>({formatFileSize(file?.size ?? 0)})</span>
            ) : (
              <div className='d-flex align-items-center gap-1 zocks-text-gray-700 flex-shrink-0'>
                <i className='bi bi-file-earmark-x fs-6' />
                <span className='paragraph-small text-nowrap'>File deleted</span>
              </div>
            )
          ) : null}
          {documentType ? (
            <div className='d-flex align-items-center gap-1 zocks-text-gray-700 flex-shrink-0'>
              <i className='bi bi-file-earmark-richtext fs-6' />
              <span className='paragraph-small text-nowrap'>{documentType.name}</span>
            </div>
          ) : null}
        </div>
        <div className='document-row-divider align-self-stretch flex-shrink-0 zocks-bg-color-gray-400' />
        <DocumentProcessingStatus document={document} />
        <div className='document-row-divider align-self-stretch flex-shrink-0 zocks-bg-color-gray-400' />
        <div className='d-flex align-items-center gap-3'>
          <i className='bi bi-trash fs-6 cursor-pointer' onClick={showDeleteModal} />
          <i className='bi bi-chevron-right fs-6' />
        </div>
      </div>
    </>
  )
}
